import type { SxProps, Theme } from "@mui/material";
import { darken } from "@mui/material/styles";

import { activityDialogTitleTextSx } from "../CreateCallPlanModal/ActivityDialog/ActivityDialog.styles";

const viewCallPlanHeaderButtonTypography = {
  fontSize: "14px",
  lineHeight: "22px",
  fontFamily: '"Museo Sans", sans-serif',
  letterSpacing: "0.08em",
  textTransform: "uppercase" as const,
  fontWeight: 700,
};

export const viewCallPlanHeaderBlackWhiteButtonSx: SxProps<Theme> = {
  width: "max-content",
  minWidth: "auto",
  py: 1.25,
  px: 2,
  height: 45,
  ...viewCallPlanHeaderButtonTypography,
  backgroundColor: "common.black",
  color: "common.white",
  border: "1px solid",
  borderColor: "common.black",
  borderRadius: "2px",
  boxShadow: "none",
  "&:hover": {
    backgroundColor: "common.white",
    color: "common.black",
    borderColor: "common.black",
    boxShadow: "none",
  },
};

export const viewCallPlanHeaderBlackWhiteButtonDisabledSx: SxProps<Theme> = {
  backgroundColor: "#e0e0e0",
  color: "#a0a0a0",
  borderColor: "#e0e0e0",
  "&:hover": {
    backgroundColor: "#e0e0e0",
    color: "#a0a0a0",
    borderColor: "#e0e0e0",
  },
};

export const viewCallPlanHeaderEditButtonSx = (canEdit: boolean): SxProps<Theme> =>
  canEdit
    ? viewCallPlanHeaderBlackWhiteButtonSx
    : ({
        ...viewCallPlanHeaderBlackWhiteButtonSx,
        ...viewCallPlanHeaderBlackWhiteButtonDisabledSx,
      } as SxProps<Theme>);

export const viewCallPlanHeaderDeleteButtonSx = (canDelete: boolean): SxProps<Theme> =>
  canDelete
    ? viewCallPlanHeaderBlackWhiteButtonSx
    : ({
        ...viewCallPlanHeaderBlackWhiteButtonSx,
        ...viewCallPlanHeaderBlackWhiteButtonDisabledSx,
      } as SxProps<Theme>);

export const viewCallPlanHeaderActionsSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  gap: 1,
  flexShrink: 0,
};

/** Meta row: value cells share a min height so text lines up with the status chip vertically. */
export const viewCallPlanMetaRowSx: SxProps<Theme> = {
  display: "flex",
  flexWrap: "wrap",
  alignItems: "stretch",
  gap: 3,
  rowGap: 2,
  mb: 2,
  pb: 2,
  borderBottom: "1px solid",
  borderColor: "divider",
};

export const viewCallPlanMetaCaptionSx: SxProps<Theme> = {
  display: "block",
  color: "text.secondary",
  fontSize: "0.75rem",
  lineHeight: 1.66,
  mb: 0.5,
};

/** Centers value content with sibling cells (matches chip vertical center). */
export const viewCallPlanMetaValueCellSx: SxProps<Theme> = {
  display: "flex",
  alignItems: "center",
  minHeight: 36,
};

/** Scroll viewport: no vertical padding here, or sticky `top: 0` sits below padding and long content can paint in that band. */
export const viewCallPlanScrollViewportSx: SxProps<Theme> = {
  flex: 1,
  minHeight: 0,
  overflow: "auto",
};

/** Padding that scrolls with the list so sticky headers can sit flush with the scrollport top. */
export const viewCallPlanScrollContentSx: SxProps<Theme> = {
  py: 2,
  px: { xs: 2, sm: 3 },
};

export const viewCallPlanModalBodySx: SxProps<Theme> = {
  flex: 1,
  minHeight: 0,
  display: "flex",
  flexDirection: "column",
  overflow: "hidden",
};

export const viewCallPlanMetaStackRoundSx: SxProps<Theme> = { minWidth: 72 };

export const viewCallPlanMetaStackWeekSx: SxProps<Theme> = { minWidth: 56 };

export const viewCallPlanMetaStackFieldSx: SxProps<Theme> = { minWidth: 120 };

export const viewCallPlanScrollSpinnerWrapSx: SxProps<Theme> = {
  display: "flex",
  justifyContent: "center",
  py: 6,
};

export const viewCallPlanSnackbarAnchorTopRight = {
  vertical: "top",
  horizontal: "right",
} as const;

export const viewCallPlanAlertFullWidthSx: SxProps<Theme> = { width: "100%" };

export const viewCallPlanActivityHeaderSx = (activityExpanded: boolean): SxProps<Theme> => ({
  display: "flex",
  alignItems: "flex-start",
  gap: 0.5,
  width: "100%",
  cursor: "pointer",
  textAlign: "left",
  border: "none",
  margin: 0,
  py: 0.75,
  pr: 0.5,
  ...(!activityExpanded ? { backgroundColor: "transparent" } : {}),
  WebkitTapHighlightColor: "transparent",
  borderRadius: activityExpanded ? 0 : 1,
  "&:hover": activityExpanded
    ? {
        bgcolor: (theme) => darken(theme.palette.background.paper, 0.05),
      }
    : { bgcolor: "action.hover" },
  "&:focus": {
    outline: "none",
  },
  "&:focus-visible": {
    outline: "2px solid",
    outlineColor: "primary.main",
    outlineOffset: 2,
  },
  ...(activityExpanded
    ? {
        position: "sticky",
        top: 0,
        zIndex: 4,
        bgcolor: "background.paper",
        borderBottom: "1px solid",
        borderColor: "divider",
        mb: 1,
      }
    : {}),
});

export const viewCallPlanActivityChevronWrapSx: SxProps<Theme> = {
  flexShrink: 0,
  display: "flex",
  alignItems: "center",
  justifyContent: "center",
  width: 32,
  height: 32,
  color: "text.secondary",
};

export const viewCallPlanActivityChevronIconSx: SxProps<Theme> = { fontSize: 22 };

export const viewCallPlanActivityHeaderTextColumnSx: SxProps<Theme> = {
  flex: 1,
  minWidth: 0,
};

export const viewCallPlanActivityTitleSx = (activityExpanded: boolean): SxProps<Theme> => ({
  ...activityDialogTitleTextSx,
  ...(activityExpanded
    ? { display: "block" }
    : {
        display: "-webkit-box",
        WebkitLineClamp: 2,
        WebkitBoxOrient: "vertical",
        overflow: "hidden",
      }),
});

/** Space between interaction blocks; keep modest so multiple expanded activities do not stack huge gaps. */
export const viewCallPlanInteractionSectionSx: SxProps<Theme> = {
  mb: 1.5,
  pb: 1.5,
  borderBottom: "1px solid",
  borderColor: "divider",
};

export const viewCallPlanInteractionTitleSx: SxProps<Theme> = {
  fontSize: "1rem",
  fontWeight: 600,
  color: "text.primary",
  mb: 1,
};

/** OtherInteraction heading: collapse default h3 margins so the row aligns with the delete icon. */
export const viewCallPlanOtherInteractionTitleSx: SxProps<Theme> = {
  ...viewCallPlanInteractionTitleSx,
  m: 0,
};

/** Stacks OtherInteraction title row and body with consistent gap. */
export const viewCallPlanOtherInteractionStackSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "column",
  gap: 1,
};

export const viewCallPlanActivitySectionLabelSx: SxProps<Theme> = {
  ...viewCallPlanInteractionTitleSx,
  display: "block",
};

export const viewCallPlanOtherHeadingRowSx: SxProps<Theme> = {
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
};

/** Title column beside optional delete control. Pass `true` when the remove icon is shown for a small optical pt nudge. */
export const viewCallPlanOtherHeadingTitleColumnSx = (withRemoveIcon: boolean): SxProps<Theme> => ({
  flex: 1,
  minWidth: 0,
  ...(withRemoveIcon ? { pt: "0.05rem" } : {}),
});

export const viewCallPlanRemoveStepIconSx: SxProps<Theme> = {
  ml: "-0.5rem",
};
